import { styled } from "styled-components";
import TextResponse from "./text-response/TextResponse";
import ModalComponent from "../modal/Modal";
import { useEffect, useRef, useState } from "react";
import ChartSelection from "./chart-selection/ChartSelection";
import DashboardList from "./dahsboard-list/DashboardList";
import GridSelection from "./grid-selection/GridSelection";
import { Button, Tooltip } from "antd";
import ResponseLoading from "./loading-response/ResponseLoading";
import InvalidInputMessage from "./text-response/InvalidInputMessage";

import {
  IDashboard,
  IDashboardFilter,
  IChartGridItemRequest,
  IPaginationFilters,
  ISearchRequest,
  ISearchPayload,
  QUERIES,
  associatePageChartGridItem,
  chartType,
  getDashboards,
  getRecentSearch,
  getSearchSession,
  openNotification,
  search,
  updateSettings,
  associatePageTableGridItem,
  useAppLayout,
  searchSettings,
  ISettings,
  IDataSource,
  Loading,
  resetSearchSession,
  checkSearchSession,
} from "@dataone/lib";
import { useMutation, useQuery } from "@tanstack/react-query";
import SearchDatatable from "./search-datatable/SearchDatatable";
import RedirectionPage from "./redirection-page/RedirectionPage";
import { useNavigate } from "react-router-dom";
import { useSmartSearch } from "../../../../contexts/smart-search.provider";
import TourSearchVisualisation from "../../../../../support/components/product-tour/TourSearchVisualisation";
import {
  CheckMarkSimpleIcon,
  HandThumbsDownIcon,
  HandThumbsUpIcon,
  LeftArrowIcon,
  UndoIcon,
} from "@dataone/icons";
import DatabaseTopics from "../database/database-topics/DatabaseTopics";
import PreviousSearches from "./recent-searches/PreviousSearches";
import { useAuth } from "../../../../../auth/contexts/auth.provider";

interface SearchResponseProps {
  selectedDB: IDataSource | null;
  selectedDBId: string | undefined;
  searchApp: string;
  response: Partial<ISearchPayload>;
  setSelectedDB: React.Dispatch<React.SetStateAction<IDataSource | null>>;
  setSelectedSearch: React.Dispatch<React.SetStateAction<{ message: string }>>;
  setResponse: React.Dispatch<React.SetStateAction<Partial<ISearchPayload>>>;
  enterPressCount: number;
  searchRef: React.RefObject<HTMLDivElement>;
}

function SearchResponse({
  response,
  searchRef,
  selectedDBId,
  searchApp,
  setSelectedDB,
  setSelectedSearch,
  setResponse,
  enterPressCount,
  selectedDB,
}: SearchResponseProps) {
  const [open, setOpen] = useState(false);
  const [showTextDetails, setShowTextDetails] = useState(false);
  const [tableExpanded, setTableExpanded] = useState(true);
  const [sentToDashboardClicked, setSendToDashboardClicked] = useState(false);
  const [openRedirectionPage, setOpenRedirectionPage] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const {
    searchMessageError,
    searchSuggestionsError,
    setShowResponse,
    setSearchMessageError,
    setSearchSuggestionsError,
  } = useSmartSearch();

  const [isSendTable, setIsSendTable] = useState(false);

  const { mutate: associateGridItem } = useMutation(associatePageChartGridItem);
  const { mutate: saveTableGrid } = useMutation(associatePageTableGridItem);
  const { mutate: getRecentHistory } = useMutation(getRecentSearch);

  const [dashboardFilters, setDashboardFilters] = useState<
    IPaginationFilters<IDashboardFilter, IDashboard>
  >({
    body: { name: "" },
    pageNo: 0,
    beforeDate: new Date(),
    sortBy: "createdAt",
    sort: "DESC",
  });

  const [selectedDashboard, setSelectedDashboard] = useState<{
    index: number;
    id: string;
    name: string;
  }>({
    index: -1,
    id: "",
    name: "",
  });

  const [selectedPage, setSelectedPage] = useState<{
    index: number;
    id: string;
    name: string;
  }>({
    index: -1,
    id: "",
    name: "",
  });

  const [gridItem, setGridItem] = useState<IChartGridItemRequest>({
    grid: [],
    x: 0,
    y: 0,
    w: 1,
    h: 1,
  });

  const { mutate: getSession } = useMutation(getSearchSession);
  const { mutate: searchSettingsMutate } = useMutation(searchSettings);

  const { mutate: editSettings, isLoading: isLoadingSaveSettings } =
    useMutation(updateSettings);

  const initialSearchRequest = {
    dataSources: selectedDBId ? [selectedDBId] : null,
    input: searchApp,
  };

  const [searchRequest, setSearchRequest] =
    useState<ISearchRequest>(initialSearchRequest);

  const navigate = useNavigate();

  const { setUser } = useAuth();
  const { mutate: checkSession } = useMutation(checkSearchSession);

  useQuery({
    queryKey: [QUERIES.RESET_SEARCH, selectedDB?.id],
    queryFn: () => resetSearchSession(selectedDB?.id!),
    enabled: !response.id && selectedDB?.reset,
    onSuccess: () => {
      checkSession(selectedDB?.id!, {
        onSuccess: () => {
          setSelectedDB((prev) => prev && { ...prev, reset: false });
        },
        onError: () => {
          useAppLayout.setState({ isResponseVisible: false });
          setShowResponse(false);
          setResponse((prev) => ({ ...prev, id: undefined }));
          setSearchMessageError("");
          setSearchSuggestionsError([]);
          openNotification("an error has occurred. please try again.", "error");
        },
      });
    },
    onError: () => {
      useAppLayout.setState({ isResponseVisible: false });
      setShowResponse(false);
      setResponse((prev) => ({ ...prev, id: undefined }));
      setSearchMessageError("");
      setSearchSuggestionsError([]);
      openNotification("an error has occurred. please try again.", "error");
    },
  });

  useQuery({
    queryKey: [QUERIES.SEARCH, searchRequest],
    queryFn: () => search(searchRequest),
    enabled: !response.id && !selectedDB?.reset,
    onSuccess: (res) => {
      if (selectedDB?.reset)
        setSelectedDB((prev) => prev && { ...prev, reset: false });
      try {
        setUser((prev) =>
          prev?.subscription
            ? {
                ...prev,
                subscription: {
                  ...prev.subscription,
                  searchUsage: prev.subscription.searchUsage - 1,
                },
              }
            : prev
        );
        setResponse((prev) => ({
          ...prev,
          id: "1",
          data: res.data === null ? [] : res.data,
          summary: res.summary === null ? "No summary found" : res.summary,
          query: res.query === null ? "" : res.query,
          searchEntity: {
            searchId: res.searchId,
          },
        }));

        if (selectedDBId) {
          searchSettingsMutate(
            {
              searchId: res.searchId,
              dataSources: [selectedDBId],
            },
            {
              onSuccess: (settingsRes) => {
                let settings: any;
                if (
                  (typeof settingsRes.settings === "string" &&
                    settingsRes.settings === null) ||
                  (typeof settingsRes.settings !== "string" &&
                    settingsRes?.settings?.chart_type === null)
                )
                  settings = {
                    chartType: chartType.DEFAULT,
                  };
                else {
                  const parsedSettings = settingsRes.settings as ISettings;
                  settings = {
                    chartType: parsedSettings.chart_type ?? chartType.DEFAULT,
                    colors: parsedSettings.colors,
                    columnName: parsedSettings.column_name,
                    rows: parsedSettings.rows,
                    hasLegend: parsedSettings.has_legend === true,
                    title: parsedSettings.title,
                    titleColor: parsedSettings.title_color,
                    subTitle: parsedSettings.sub_title,
                    subTitleColor: parsedSettings.sub_title_color,
                    lineFillColor: parsedSettings.line_fill_color === true,
                    lineChartType: parsedSettings.line_chart_type,
                    target: parsedSettings.target === true,
                    targetValue: parsedSettings.target_value,
                    underTargetColors: parsedSettings.under_target_colors,
                    stackedBar: parsedSettings.stacked_bar === true,
                    barRowLine: parsedSettings.bar_row_line,
                    barRowLineColor: parsedSettings.bar_row_line,
                    isDonut: parsedSettings.donut === true,
                    donutLabel: parsedSettings.donut_label,
                    cardValuePosition: parsedSettings.card_value_position,
                    cardIntroValue: parsedSettings.card_intro_value,
                    cardOutroValue: parsedSettings.card_outro_value,
                    // default card settings
                    cardIntroBold: true,
                    cardIntroFontSize: 27,
                    cardValueBold: true,
                    cardValueColor: "#4974f6",
                    cardValueFontSize: 35,
                    cardImage:
                      "https://api-preprod.onegate.ai/api/storage/public/20240418093156627_ai2.png",
                    // default chart settings
                    enableFormattedNumbers: true,
                    displayChartLabels: true,
                  };
                }

                const suggestion =
                  typeof settingsRes.question_suggestions === "string" &&
                  settingsRes.question_suggestions === null
                    ? []
                    : settingsRes.question_suggestions;
                const suggestion_question_settings =
                  typeof settingsRes.settings_suggestions === "string" &&
                  settingsRes.settings_suggestions === null
                    ? []
                    : settingsRes.settings_suggestions;

                setResponse((prev) => ({
                  ...prev,
                  ...settings,
                  suggestion,
                  suggestion_question_settings,
                }));

                getSession(selectedDBId, {
                  onSuccess: (sessionData) => {
                    setResponse((prev) => ({
                      ...prev,
                      id: sessionData.searchId,
                      settingsId: sessionData.settingsId,
                    }));
                  },
                });
              },
              onError: () => {
                setResponse((prev) => ({
                  ...prev,
                  chartType: chartType.DEFAULT,
                }));
              },
            }
          );

          getRecentHistory(
            { body: selectedDBId, pageNo: 0 },
            {
              onSuccess: (history) => {
                setResponse((prev) => ({
                  ...prev,
                  recentSearches: history.content,
                }));
              },
            }
          );
        }
      } catch (err: any) {
        setResponse((prev) => ({
          ...prev,
          id: "2",
        }));
        setSearchMessageError(() => err.response.data.message);
      }
    },
    onError: (err: any) => {
      if (selectedDB?.reset)
        setSelectedDB((prev) => prev && { ...prev, reset: false });
      setResponse((prev) => ({
        ...prev,
        id: "2",
      }));
      setSearchMessageError(() => err.response.data.message);
      if (err.suggestions)
        setSearchSuggestionsError(() => err.response.data.suggestions);
    },
  });

  const {
    data: dashboards,
    isLoading: isDashboardLoading,
    refetch: refetchDashboards,
  } = useQuery({
    queryKey: [QUERIES.DASHBOARDS, dashboardFilters],
    queryFn: () => getDashboards(dashboardFilters),
  });

  const handleCancel = () => {
    setOpen(false);
    setSendToDashboardClicked(false);
    setOpenRedirectionPage(false);
  };
  const handleOk = () => {
    editSettings({ ...response, id: response.settingsId! } as ISearchPayload, {
      onSuccess: () => {
        setOpen(true);
        setIsSendTable(false);
        setSendToDashboardClicked(true);
        // openNotification("Settings updated successfully", "info");
        // setSendToDashboardClicked(true);
      },
    });
  };

  const confirmSendToDashboard = () => {
    setConfirmLoading(true);
    response?.settingsId &&
      !isSendTable &&
      associateGridItem(
        {
          gridItem,
          pageId: selectedPage.id,
          settingsId: response.settingsId,
        },
        {
          onSuccess: () => {
            // setOpen(false);
            // openNotification("Grid added successfully to the page");
            setSendToDashboardClicked(false);
            setOpenRedirectionPage(true);
            setGridItem({
              grid: [],
              x: 0,
              y: 0,
              w: 1,
              h: 1,
            });
            setConfirmLoading(false);
            // navigate(
            //   `/dashboard/edit/${selectedDashboard.id}/${selectedPage.id}`
            // );
          },
          onError: () => {
            setConfirmLoading(false);
          },
        }
      );
    response?.settingsId &&
      isSendTable &&
      saveTableGrid(
        {
          gridItem,
          pageId: selectedPage.id,
          settingsId: response.settingsId,
        },
        {
          onSuccess: () => {
            // setOpen(false);
            // openNotification("Grid added successfully to the page");

            setSendToDashboardClicked(false);
            setOpenRedirectionPage(true);
            setGridItem({
              grid: [],
              x: 0,
              y: 0,
              w: 1,
              h: 1,
            });
            setConfirmLoading(false);
            // setConfirmLoading(false);
            // navigate(
            //   `/dashboard/edit/${selectedDashboard.id}/${selectedPage.id}`
            // );
          },
          onError: () => {
            setConfirmLoading(false);
          },
        }
      );
  };

  const redirectToDashboard = () => {
    setOpen(false);
    useAppLayout.setState({ isSmartSearchVisible: false });
    useAppLayout.setState({ isResponseVisible: false });
    navigate(`/dashboard/edit/${selectedDashboard.id}/${selectedPage.id}`, {
      state: { isRefreshing: true },
    });
  };
  useEffect(() => {
    if (!searchApp) return;
    setSearchMessageError(() => "");
    setSearchSuggestionsError(() => []);
    setResponse((prev) => ({
      ...prev,
      id: "",
      chartType: undefined,
      columnName: undefined,
      rows: undefined,
      hasLegend: undefined,
      colors: undefined,
      title: undefined,
      titleBold: undefined,
      titleColor: undefined,
      titleUnderline: undefined,
      titleItalic: undefined,
      subTitle: undefined,
      subTitleBold: undefined,
      subTitleColor: undefined,
      subTitleUnderline: undefined,
      subTitleItalic: undefined,
      data: undefined,
      summary: undefined,
      lineFillColor: undefined,
      lineChartType: undefined,
      target: undefined,
      targetValue: undefined,
      underTargetColors: undefined,
      stackedBar: undefined,
      barRowLine: undefined,
      barRowLineColor: undefined,
      donut: undefined,
      donutLabel: undefined,
    }));
    setSearchRequest((prev) => ({
      ...prev,
      input: searchApp,
    }));
  }, [searchApp, enterPressCount]);

  const openModal = () => {
    handleOk();
  };

  const handleGoBack = () => {
    if (openRedirectionPage) {
      setOpenRedirectionPage(false);
      setSendToDashboardClicked(true);
    } else {
      setSendToDashboardClicked(false);
    }
  };

  const handleSendTableToDashboard = () => {
    setIsSendTable(true);
    setSendToDashboardClicked(true);
    setOpen(true);
  };

  const [rows, setRows] = useState<number[]>([]);
  const [columns, setColumns] = useState<number[]>([]);
  const [isLiked, setIsLiked] = useState(false);
  const [isDisLiked, setIsDisLiked] = useState(false);

  const handleLike = () => {
    setIsLiked(!isLiked);
    isDisLiked && setIsDisLiked(false);
  };
  const handleDisLike = () => {
    setIsDisLiked(!isDisLiked);
    isLiked && setIsLiked(false);
  };

  const footer = (
    <FooterContainer>
      {/* <div className="footer"></div> */}
      <StyledFooter>
        <StyledButton
          type="default"
          onClick={handleCancel}
          className="cancel-btn"
        >
          <UndoIcon />
        </StyledButton>
        <StyledButton
          disabled={isLoadingSaveSettings}
          type="primary"
          onClick={handleOk}
          className={isLoadingSaveSettings ? "ok-btn-hover" : "ok-btn"}
        >
          {isLoadingSaveSettings ? "Saving ..." : "Save & Send to Dashboard"}
        </StyledButton>
      </StyledFooter>
    </FooterContainer>
  );

  const SendToDashboardfooter = (
    <StyledSentToDashboardFooter>
      {/* {!isSendTable && (
        <StyledsentToDashboardButton
          type="default"
          onClick={handleGoBack}
          className="previous-btn"
        >
          Previous
        </StyledsentToDashboardButton>
      )} */}
      {(rows.length < 1 && columns.length < 1) || selectedPage.index === -1 ? (
        ""
      ) : (
        <StyledButton
          type="default"
          onClick={confirmSendToDashboard}
          className="confirm-btn"
          confirmLoading={confirmLoading}
        >
          {confirmLoading ? "Confirming ..." : "Confirm"}
        </StyledButton>
      )}
    </StyledSentToDashboardFooter>
  );

  const stepRef1 = useRef(null);
  const stepRef2 = useRef(null);
  const stepRef3 = useRef(null);
  const stepRef4 = useRef(null);

  const { isSmartSearchVisible } = useAppLayout();

  const showSmartSearch = () => {
    useAppLayout.setState({ isSmartSearchVisible: true });
  };

  const hideResponse = () => {
    if (isSmartSearchVisible) {
      useAppLayout.setState({ isResponseVisible: false });
      setShowResponse(false);
      setResponse((prev) => ({ ...prev, id: undefined }));
      setSearchMessageError("");
      setSearchSuggestionsError([]);
    } else showSmartSearch();
  };

  return (
    // <TourSearchVisualisation
    //   stepRef1={stepRef1}
    //   stepRef2={stepRef2}
    //   stepRef3={stepRef3}
    //   stepRef4={stepRef4}
    // >
    <StyledContent>
      {!response.id ? (
        <ResponseLoading />
      ) : (
        <div>
          <div>
            {searchMessageError ? (
              <div className="head">
                <InvalidInputMessage
                  message={searchMessageError}
                  suggestions={searchSuggestionsError}
                  setSelectedSearch={setSelectedSearch}
                  setResponse={setResponse}
                />
              </div>
            ) : (
              <>
                <StyledTitle>
                  {response?.chartType ? (
                    <StyledCheckMarkSimpleIcon>
                      <CheckMarkSimpleIcon />
                    </StyledCheckMarkSimpleIcon>
                  ) : (
                    <img
                      alt="avatar"
                      src={"images/smart-search/chat/OG.gif"}
                      className="avatar"
                    />
                  )}
                  <div>Search</div>
                  <StyledReset onClick={hideResponse}>
                    <StyledLeftArrowIcon>
                      <LeftArrowIcon />
                    </StyledLeftArrowIcon>
                    <div className="reset">START A NEW SEARCH</div>
                  </StyledReset>
                </StyledTitle>
                {selectedDB?.dataSourceType === "DATABASE" && (
                  <div className="topics">
                    <DatabaseTopics selectedDB={selectedDB} />
                  </div>
                )}
                <div className="head">
                  <div>
                    <TextResponse
                      response={response}
                      showTextDetails={showTextDetails}
                      toggleShowTextDetails={setShowTextDetails}
                    />
                  </div>
                  {/* {response.chartType !== chartType.DEFAULT && (
                      <div
                        ref={stepRef2}
                        className="chart-card"
                        {...(response.chartType && {
                          onClick: () => openModal(),
                        })}
                      >
                        {!showTextDetails && (
                          <ChartResponse
                            isLoading={!response.chartType}
                          ></ChartResponse>
                        )}
                      </div>
                    )} */}
                </div>

                <SearchDatatable
                  stepRef1={stepRef1}
                  handleSendTableToDashboard={handleSendTableToDashboard}
                  data={response.data}
                  settingId={response.settingsId!}
                  showTextDetails={showTextDetails}
                />
                {response.chartType !== chartType.DEFAULT &&
                  (!response.chartType ? (
                    <StyledChartLoading>
                      <Loading />
                    </StyledChartLoading>
                  ) : (
                    <ChartSelection
                      stepRef3={stepRef3}
                      searchApp={searchApp}
                      response={response}
                      setResponse={setResponse}
                      tableExpanded={tableExpanded}
                      selectedDbId={selectedDBId}
                      isLoadingSaveSettings={isLoadingSaveSettings}
                      openModal={openModal}
                    />
                  ))}
                <StyledFeedback>
                  <p>How did we do with this result</p>
                  <Tooltip
                    placement="bottom"
                    title={
                      <span style={{ fontSize: "12px" }}>Good Answer</span>
                    }
                    overlayStyle={{ maxWidth: "300px" }}
                  >
                    <StyledHandUpIcon onClick={handleLike} isLiked={isLiked}>
                      <HandThumbsUpIcon />
                    </StyledHandUpIcon>
                  </Tooltip>
                  <Tooltip
                    placement="bottom"
                    title={
                      <span style={{ fontSize: "12px" }}>Wrong Answer</span>
                    }
                    overlayStyle={{ maxWidth: "300px" }}
                  >
                    <StyledHandDownIcon
                      onClick={handleDisLike}
                      isDisLiked={isDisLiked}
                    >
                      <HandThumbsDownIcon />
                    </StyledHandDownIcon>
                  </Tooltip>
                </StyledFeedback>
                <div>
                  <PreviousSearches
                    searchRef={searchRef}
                    selectedDBId={selectedDBId}
                    setResponse={setResponse}
                  />
                </div>
              </>
            )}
          </div>
          <div>
            <ModalComponent
              open={open}
              isSendToDashboard={sentToDashboardClicked ? true : false}
              isRedirectPage={openRedirectionPage}
              footer={
                sentToDashboardClicked
                  ? SendToDashboardfooter
                  : openRedirectionPage
                  ? null
                  : footer
              }
              children1={
                sentToDashboardClicked ? (
                  <DashboardList
                    dashboardFilters={dashboardFilters}
                    setDashboardFilters={setDashboardFilters}
                    selectedDashboard={selectedDashboard}
                    setSelectedDashboard={setSelectedDashboard}
                    isLoading={isDashboardLoading}
                    dashboards={dashboards}
                    setSelectedPage={setSelectedPage}
                    selectedPage={selectedPage}
                    refetchDashboards={refetchDashboards}
                  />
                ) : openRedirectionPage ? (
                  <RedirectionPage
                    selectedDashboard={selectedDashboard}
                    selectedPage={selectedPage}
                    handleCancel={handleCancel}
                    handleGoBack={handleGoBack}
                    redirectToDashboard={redirectToDashboard}
                    isSendTable={isSendTable}
                  />
                ) : null
                // <DetailsTable
                //   response={response}
                //   selectedDbId={selectedDBId}
                //   searchApp={searchApp}
                //   tableExpanded={tableExpanded}
                // />
              }
              children2={
                sentToDashboardClicked || openRedirectionPage
                  ? // <DashboardPages
                    //   selectedDashboard={selectedDashboard}
                    //   selectedPage={selectedPage}
                    //   setSelectedPage={setSelectedPage}
                    //   isLoading={isDashboardLoading}
                    //   dashboards={dashboards}
                    // />
                    null
                  : response && (
                      // <ChartSelection
                      //   stepRef3={stepRef3}
                      //   searchApp={searchApp}
                      //   response={response}
                      //   setResponse={setResponse}
                      //   tableExpanded={tableExpanded}
                      //   selectedDbId={selectedDBId}
                      // />
                      <></>
                    )
              }
              children3={
                <GridSelection
                  setGridItem={setGridItem}
                  pageId={selectedPage.id}
                  selectedPage={selectedPage}
                  selectedDashboard={selectedDashboard}
                  rows={rows}
                  setRows={setRows}
                  columns={columns}
                  setColumns={setColumns}
                />
                // sentToDashboardClicked ? (
                //   <GridSelection
                //     setGridItem={setGridItem}
                //     pageId={selectedPage.id}
                //     selectedPage={selectedPage}
                //     selectedDashboard={selectedDashboard}
                //     rows={rows}
                //     setRows={setRows}
                //     columns={columns}
                //     setColumns={setColumns}
                //   />
                // ) : openRedirectionPage ? null : (
                //   <DataTable
                //     // stepRef4={stepRef4}
                //     searchApp={searchApp}
                //     setResponse={setResponse}
                //     response={response}
                //     // setTableExpanded={setTableExpanded}
                //     // tableExpanded={tableExpanded}
                //   />
                // )
              }
              onCancel={handleCancel}
              onOk={handleOk}
            />
          </div>
        </div>
      )}
    </StyledContent>
    // </TourSearchVisualisation>
  );
}

export default SearchResponse;

const StyledSentToDashboardFooter = styled.div`
  margin-right: 10px;
  display: flex;
  // align-items: center;
`;
const StyledContent = styled.div`
  width: 70vw;
  margin: 10px 1px 24px 1px;
  max-width: 1300px;
  h5 {
    color: #040726;
    font-size: 13px;
    font-weight: 700;
    // padding: 10px 0px 0px 10px;
    // margin: 0px;
  }
  .head {
    display: flex;
    flex-direction: row;

    > div:nth-child(1) {
      flex: 1;
    }
    )
    .chart-card {
      margin-left: auto;
    }
  }
  .topics {
    position: relative;
    z-index: 1;
    overflow: hidden;

    &:before,
    &:after {
      content: "";
      position: absolute;
      top: 0;
      height: 100%;
      z-index: 2;
    }

    &:before {
      left: -5px;
      background: linear-gradient(to left, rgba(255, 255, 255, 0), #ffffff);
      width: 20px;
    }

    &:after {
      right: 0;
      background: linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff);
      width: 20px;
    }
  }
`;
const StyledFooter = styled.div`
  // margin-top: 0px !important;
  // position: absolute;
  // bottom: 25px;
  // display: flex;
  // margin-block: 30px 15px;
  // margin: 0px;
  display: flex;
  align-items: center;
  margin-top: 10px;
  // margin-right: 10px;
  margin-bottom: 12px;
  // margin-right: 10px;
  // margin-left: 73%;
  // justify-content: flex-end;
`;

const StyledButton = styled(Button)<{ confirmLoading?: boolean }>`
  margin-left: 10px;
  &.ok-btn {
    border-radius: 10px;
    background: #4974f6;
    color: #eff1fd;
    font-size: 12px;
    font-weight: 400;
    height: 35px;
    border: none;
    &:hover {
      background-color: #fff !important;
      color: #4974f6 !important;
      border: none;
      box-shadow: none;
    }
  }
  &.ok-btn-hover {
    background-color: #fff !important;
    color: #4974f6 !important;
    border: none;
    box-shadow: none;
  }
  &.cancel-btn {
    display: flex;
    color: #1c1b1f;
    font-size: 20px;
    border-radius: 10px;
    background: #eff1fd;
    // font-size: 13px;
    font-weight: 400;
    height: 36px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    border: none;
    &:hover {
      background: #fbd8dc;
      border: none;
      color: #1c1b1f !important;
    }
  }
  &.confirm-btn {
    border-radius: 10px;
    background: #4974f6;
    color: #eff1fd;
    font-size: 13px;
    font-weight: 400;
    border: none;
    height: 35px;
    display: flex;
    margin-left: auto;
    padding-top: 8px;
    margin-top: 8px;
    cursor: ${(props) => (props.confirmLoading ? "not-allowed" : "pointer")};
    &:hover {
      border: none;
      color: #fff !important;
    }
  }
`;

const StyledsentToDashboardButton = styled(Button)`
  &.previous-btn {
    border-radius: 10px;
    background: #eff1fd;
    color: #040726;
    font-size: 12px;
    font-weight: 400;
    border: none;
    height: 35px;
    display: flex;
    margin-left: auto;
    padding-top: 8px;
    margin-top: 8px;
    margin-right: 10px;
    &:hover {
      border: none;
      color: #000 !important;
    }
  }
`;
const FooterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  .footer {
    min-width: 649px;
  }
`;
const StyledHandUpIcon = styled.div<{ isLiked: boolean }>`
  cursor: pointer;
  display: flex;
  width: 25px;
  margin-left: 20px;
  color: ${({ isLiked }) => (isLiked ? "#00FF00" : "#040726")};
`;

const StyledHandDownIcon = styled.div<{ isDisLiked: boolean }>`
  cursor: pointer;
  display: flex;
  width: 25px;
  color: ${({ isDisLiked }) => (isDisLiked ? "#FF0000" : "#040726")};
`;

const StyledFeedback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 15px;
  .title {
    color: #04072699;
    font-size: 12px;
    width: 131px;
    margin-left: 10px;
  }
  p {
    padding: 4px 14px;
    border: 1.5px solid #c1c4d4;
    border-radius: 30px;
  }
`;

const StyledChartLoading = styled.div`
  height: 200px;
`;

const StyledTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: #040726;
  font-size: 15px;
  font-weight: 300;
  padding: 5px;
  margin-bottom: 10px;

  span {
    font-size: 16px;
    font-weight: 500;
  }

  img {
    height: 25px;
    width: 25;
    margin-right: 5px;
  }
  // svg {
  //   font-size: 25px;
  //   margin-right: 5px;
  //   color: green;
  // }
`;

const StyledLeftArrowIcon = styled.div`
  display: flex;
  font-size: 25px;
  color: #4974f6;
  border: 1.5px solid #4974f6;
  border-radius: 20px;
  margin-right: 5px;
  position: absolute;
  width: 28px;
  transition: width 0.5s ease-in-out;
  svg {
    transform: rotate(180deg);
  }
`;
const StyledReset = styled.div`
  margin-left: auto;
  font-size: 14px;
  display: flex;
  align-items: center;
  border-radius: 20px;
  cursor: pointer;
  width: 180px;
  position: relative;
  &:hover {
    ${StyledLeftArrowIcon} {
      width: 183px;
      transition: width 0.5s ease-in-out;
    }
  }

  .reset {
    margin-left: 33px;
  }
`;

const StyledCheckMarkSimpleIcon = styled.div`
  font-size: 25px;
  margin-right: 5px;
  color: green;
  display: flex;
`;
